import { ClareSystem, ClareSystemData, ClareSystemHelper } from "@app/project/model/clare-system";
import { TSLogActions, TSLogObjectTypes, TSLoggingService } from "@app/shared/services/ts-logging.service";
import { User, UserHelper } from "@app/user/model/user";
import { Observable, forkJoin, of, throwError } from "rxjs";
import { catchError, map, mergeMap, switchMap, take, tap } from "rxjs/operators";
import { CentralStationProject, CentralStationProjectHelper } from "../model/central-station-project";
import { Project, ProjectAndRelatedData, ProjectData, ProjectHelper } from "../model/project";
import { SecuritySystem, SecuritySystemData } from "../model/security-system";

import { Injectable } from "@angular/core";
import { Address } from "@app/address/model/address";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { CustomerPortalService } from "@app/customer-portal/services/customer-portal.service";
import { Customer } from "@app/customer/model/customer";
import { ClassImporterExporter } from "@app/shared/resources/class-importer-exporter.service";
import { AppHelperService } from "@app/shared/services/app-helper.service";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { DbCrudHelper } from "@app/shared/services/db-crud-helper";
import { UserDbService } from "@app/user/service/user-db.service";
import { AdditionalFeatureData, AssignedProjectFeatureData } from "../model/project-feature";
import { ProjectActions } from "../store";
import { ProjectDispatchers } from "../store/dispatchers/project.dispatchers";
import { ProjectAndRelated } from "./clare-system-db.service";
import { SecuritySystemDbService } from "./security-system-db.service";

export type CreateProjectPayload = Omit<Project, "id">;

export interface PartialClareSystemUser {
    id: number;
    email: number;
    git_id: string;
}

export interface UserClareSystemData {
    claresystem_id: number;
    userinfo_id: number;
}

interface CreateControllerResponse {
    claresystem: ClareSystemData;
    project: ProjectData;
    securitysystem: SecuritySystemData | null;
}

export interface ProjectIdWithUuid {
    project_id: number;
    uuid: string | null;
}

@Injectable({
    providedIn: "root",
})
export class ProjectDbService {
    helper = new ProjectHelper();
    userHelper = new UserHelper();
    wsHelper: ClareWSCrudHelperService<Project, ProjectData>;

    constructor(
        private tsLoggingService: TSLoggingService,
        private crudHelper: DbCrudHelper,
        private securitySystemDbService: SecuritySystemDbService,
        private userDbService: UserDbService,
        private customerPortalService: CustomerPortalService,
        private auditLogService: AuditLogService,
        private projectDispatcher: ProjectDispatchers
    ) {
        this.wsHelper = new ClareWSCrudHelperService(this.auditLogService, crudHelper, this.helper);
    }

    saveCustomerDetails(customerData: User): Observable<User> {
        if (!customerData.id) {
            return this.userDbService.createUser({ user: customerData }).pipe(map(({ user }) => user));
        }
        return this.userDbService.updateUser({ user: customerData }).pipe(map(({ user }) => user));
    }

    updateProject(project: Project, claresystem: ClareSystem, securitysystem: SecuritySystem) {
        // TODO: figure out where this get's hit and test it.

        let updatedProject = Object.assign(project, {});
        if (claresystem?.id) {
            updatedProject = { ...updatedProject, claresystemId: claresystem.id, claresystem };
        }

        if (securitysystem?.id) {
            updatedProject = { ...updatedProject, securitysystemId: securitysystem.id, securitysystem };
        }

        updatedProject = Object.assign(new ProjectHelper(), updatedProject);

        return this.updateProject$(updatedProject).pipe(map(() => updatedProject));
    }

    upsertController(action: ProjectActions.CreateProjectController | ProjectActions.UpdateProjectController) {
        const controllerData: ClareSystemData = ClassImporterExporter.exportObject(action.claresystem, ClareSystemHelper.importMap);
        let securityData = null;

        if (action.securitysystem?.imei || action.securitysystem?.iccid || action.securitysystem?.macAddress) {
            securityData = ClassImporterExporter.exportObject(action.securitysystem, SecuritySystem.importMap);
        }

        const payload = {
            claresystem: controllerData,
            securitysystem: securityData,
            project: {
                id: action.project.id,
            },
        };

        return this.createController(payload);
    }

    createController(payload: { project: Partial<Project>; claresystem?: ClareSystemData; securitysystem?: SecuritySystemData }) {
        return this.crudHelper
            .makeRequest<CreateControllerResponse>("CreateController", payload)
            .pipe(map(result => this.compileUpdatedProject(result)));
    }

    private compileUpdatedProject(response: CreateControllerResponse) {
        const updatedProject: Project = new ProjectHelper();
        const project = ClassImporterExporter.importObject(response.project, ProjectHelper.importMap);
        const securitysystem = ClassImporterExporter.importObject(response.securitysystem, SecuritySystem.importMap);
        const claresystem = ClassImporterExporter.importObject(response.claresystem, ClareSystemHelper.importMap);
        if (project?.id) {
            [claresystem, securitysystem].forEach((system, index) => {
                if (system?.id) {
                    switch (index) {
                        case 0:
                            project.claresystemId = claresystem.id;
                            project.claresystem = claresystem;
                            break;
                        case 1:
                            project.securitysystemId = securitysystem.id;
                            project.securitysystem = securitysystem;
                            break;
                    }
                }
            });
            return Object.assign(updatedProject, project);
        }
        return updatedProject;
    }

    upsertSecuritySystem(securitysystem: SecuritySystem, project: Project): Observable<SecuritySystem> {
        if (securitysystem) {
            if (securitysystem.macAddress) {
                securitysystem.macAddress = securitysystem.macAddress.toUpperCase();
            }
            if (securitysystem.id) {
                return this.updateSecuritySystem(securitysystem);
            }
            const securitysystemDbObj = ClassImporterExporter.exportObject(securitysystem, SecuritySystem.importMap);
            return this.createController({ project, securitysystem: securitysystemDbObj }).pipe(map(project => project.securitysystem));
        }
        return of(securitysystem);
    }

    getCentralStationProjectData(macAddresses: string[]): Observable<CentralStationProject[]> {
        return this.getSecuritysystemsByMacAddresses(macAddresses).pipe(
            map(projectData => {
                if (projectData?.length) {
                    return (
                        projectData
                            ?.map(project => CentralStationProjectHelper.fromProjectAndRelatedData(project))
                            .sort((a, b) => b?.name.toLowerCase().localeCompare(a?.name.toLowerCase())) || []
                    );
                }
                return [];
            })
        );
    }

    getSecuritysystemsByMacAddresses(macAddresses: string[]) {
        return this.crudHelper.makeRequest<ProjectAndRelatedData[]>("GetProjectAndRelatedByMacAddresses", {
            mac_addresses: macAddresses,
        });
    }

    private updateSecuritySystem(securitysystem: SecuritySystem) {
        return this.securitySystemDbService.updateSystem(securitysystem).pipe(map(_ => securitysystem));
    }

    connectUserToClaresystem(customer: Customer, claresystem: ClareSystem) {
        return this.customerPortalService.selectLocationUsers$(claresystem).pipe(
            take(1),
            mergeMap(users => {
                if (!users.some(user => user.userinfo.id === customer.id)) {
                    return this.crudHelper.makeRequest<any>("CreateUserinfoClaresystem", {
                        userinfo_id: customer.id,
                        claresystem_id: claresystem.id,
                    });
                }
                return of(null);
            }),
            tap(() => this.customerPortalService.clearLocationUsers(claresystem)),
            catchError(e => throwError(() => e))
        );
    }

    checkForServicePlan() {
        return false; // this will be replaced by something earth-shattering.
    }

    deleteProject$(project: Project): Observable<Project> {
        const auditLogName = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.DELETE);
        const auditData = `${project.name ? project.name.split(",")[0].trim() : "No Customer"}, ${project.street || "No Address"}`;
        return this.auditLogService
            .logEventWithoutEndTime(auditLogName, {
                data: `Account Deleted: ${auditData}`,
                entityId: project.entityId,
                projectId: project.id,
            })
            .pipe(
                take(1),
                catchError(_ => of(undefined)), // allows delete operation to continue even if audit log fails
                switchMap(createdLog =>
                    this.wsHelper
                        .delete({
                            endpoint: "DeleteProject",
                            id: project.id,
                            auditLogName,
                            logData: createdLog
                                ? {
                                      endTime: AppHelperService.getCurrentDate(),
                                      id: createdLog.id,
                                      value: createdLog.value,
                                  }
                                : null,
                        })
                        .pipe(
                            map(() => project),
                            catchError(error => {
                                // update created log if it was returned to show failure
                                if (createdLog) {
                                    this.auditLogService.logEvent(
                                        auditLogName,
                                        {
                                            endTime: AppHelperService.getCurrentDate(),
                                            id: createdLog.id,
                                            value: `Failed to Delete Account: ${auditData}`,
                                        },
                                        true
                                    );
                                }

                                return throwError(() => error);
                            })
                        )
                )
            );
    }

    deleteUserinfoClaresystem(userinfoId: number, claresystemId: number) {
        if (userinfoId && claresystemId) {
            return this.crudHelper
                .makeRequest<any>("DeleteUserinfoClaresystem", {
                    userinfo_id: userinfoId,
                    claresystem_id: claresystemId,
                })
                .pipe(take(1), this.crudHelper.throwIfZeroUpdates(), catchError(DataServiceError.handleError()));
        }

        return of(null);
    }

    deleteAllUserInfoBySystem(claresystemId: number) {
        if (claresystemId) {
            return this.crudHelper
                .makeRequest<any>("DeleteUserinfoClaresystem", {
                    claresystem_id: claresystemId,
                })
                .pipe(take(1), this.crudHelper.throwIfZeroUpdates(), catchError(DataServiceError.handleError()));
        }

        return of(null);
    }

    getProjectUsers$(projectId: number): Observable<PartialClareSystemUser[]> {
        return this.crudHelper.makeRequest<PartialClareSystemUser[]>("GetProjectClaresystemUsers", { id: projectId });
    }

    createProject$(project: ProjectData, address?: Address): Observable<ProjectData> {
        return this.crudHelper.makeRequest<ProjectData>("CreateProject", { project, address });
    }

    getProjectsByEntityAndUser$(entityId: number, userId: number): Observable<number[]> {
        return this.crudHelper.makeRequest<number[]>("GetProjectsByEntityAndUser", { entity_id: entityId, userinfo_id: userId });
    }

    getProjectsByEntity$(id: number): Observable<number[]> {
        return this.crudHelper.makeRequest<number[]>("GetProjectsByEntity", { id });
    }

    getProjectAndRelated$(id: number): Observable<(Project & { primaryUser?: User }) | null> {
        return this.getProjectAndRelated(id).pipe(
            this.crudHelper.returnNullIfNotFound(),
            map(data => {
                // New API can return default securitysystem that we should ignore
                if (!!data) {
                    const projectAndRelatedData = new ProjectAndRelatedData(data);
                    const { userinfo1 } = projectAndRelatedData;
                    const projectObj: Project = {
                        ...projectAndRelatedData.getParsedProject(),
                        claresystem: projectAndRelatedData.getParsedClareSystem(),
                        address: projectAndRelatedData.getParsedAddress(),
                        securitysystem: projectAndRelatedData.getParsedSecuritySystem(),
                        isOccupied: !!userinfo1?.id,
                    };
                    return { ...projectObj, primaryUser: projectAndRelatedData.getPrimaryUserInfo() };
                }

                return null;
            })
        );
    }

    getProjectAndRelatedByUuid(uuid: string): Observable<ProjectAndRelated | null> {
        return this.crudHelper.makeRequest<ProjectAndRelatedData>("GetProjectAndRelatedByUUID", { uuid }).pipe(
            this.crudHelper.returnNullIfNotFound(),
            map(data => {
                if (!data) {
                    return null;
                }

                const projectAndRelatedData = new ProjectAndRelatedData(data);
                const project = projectAndRelatedData.getParsedProject();
                const clareSystem = projectAndRelatedData.getParsedClareSystem();
                const securitysystem = projectAndRelatedData.getParsedSecuritySystem();
                const address = projectAndRelatedData.getParsedAddress();

                return { address, clareSystem, project, securitysystem };
            }),
            catchError(DataServiceError.handleError())
        );
    }

    updateProject$(project: Project) {
        return this.wsHelper.update({
            endpoint: "UpdateProject",
            data: project,
            logData: { project },
        });
    }

    activateProject$(projectId: number) {
        const logData = { project: { id: projectId } };
        this.tsLoggingService.logEvent(null, this.helper.dbTypeName, TSLogObjectTypes.ssopgrest, TSLogActions.edit, null, logData);
        return this.crudHelper.makeRequest<any>("ActivateProject", {
            id: projectId,
        });
    }

    deactivateProject$(projectId: number) {
        const logData = { project: { id: projectId } };
        this.tsLoggingService.logEvent(null, this.helper.dbTypeName, TSLogObjectTypes.ssopgrest, TSLogActions.edit, null, logData);
        return this.crudHelper.makeRequest<any>("DeactivateProject", {
            id: projectId,
        });
    }

    getEntityUuids$(entityId: number): Observable<ProjectIdWithUuid[]> {
        return this.crudHelper.makeRequest<ProjectIdWithUuid[] | null>("GetEntityUUIDs", { id: entityId }).pipe(
            tap(result => {
                if (result === null) {
                    console.error(`No company exists with id ${entityId}`);
                    throw new DataServiceError(`Invalid entityId=${entityId}`);
                }
            })
        );
    }

    /**
     * Enable feature
     *
     * @param project_id
     * @param feature_id
     * @returns
     */
    addProjectFeatures(project_id: number, feature_id: number) {
        const data = { project_id, feature_id };
        this.tsLoggingService.logEvent(null, "Project Feature", TSLogObjectTypes.ssopgrest, TSLogActions.add, null, { data });
        return this.crudHelper.makeRequest<any>("CreateProjectFeature", { project_id, feature_id });
    }

    /**
     * disable feature, might need to update in future
     *
     * @param project_id
     * @param feature_id
     * @returns
     */
    removeProjectFeatures(project_id: number, feature_id: number) {
        const data = { project_id, feature_id };
        this.tsLoggingService.logEvent(null, "Project Feature", TSLogObjectTypes.ssopgrest, TSLogActions.delete, null, { data });
        return this.crudHelper.makeRequest<any>("DeleteProjectFeature", { project_id, feature_id });
    }

    getProjectFeatures(projectId: number) {
        return this.crudHelper.makeRequest<AssignedProjectFeatureData[]>("GetProjectFeatures", { id: projectId });
    }

    getAllAdditionalFeatures() {
        return this.crudHelper.makeRequest<AdditionalFeatureData[]>("GetAllFeatures");
    }

    updateFeaturesStatus(features, uuid: string, project: Project) {
        const featureObservables: Observable<any>[] = [];

        for (const feature of features) {
            if (feature.enable_new_feature === null) {
                continue;
            }

            if (!!feature.enable_new_feature) {
                featureObservables.push(
                    this.addProjectFeatures(feature.project_id, feature.id).pipe(
                        take(1),
                        tap(_ => {
                            const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.FEATURES, LOG_SUB_TYPE.ENABLE);
                            this.auditLogService.logEvent(logKey, {
                                data: `Feature Name: ${feature.featureName}`,
                                projectId: project.id,
                                entityId: project.entityId,
                                optionalParams: { uuid },
                            });
                        })
                    )
                );
            } else {
                featureObservables.push(
                    this.removeProjectFeatures(feature.project_id, feature.id).pipe(
                        take(1),
                        tap(_ => {
                            const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.FEATURES, LOG_SUB_TYPE.DISABLE);
                            this.auditLogService.logEvent(logKey, {
                                data: `Feature Name: ${feature.featureName}`,
                                projectId: project.id,
                                entityId: project.entityId,
                                optionalParams: { uuid },
                            });
                        })
                    )
                );
            }
        }

        forkJoin(featureObservables)
            .pipe(
                take(1),
                tap(_ => {
                    this.projectDispatcher.clearProjectFeatureState();
                })
            )
            .subscribe();
    }
    private getProjectAndRelated(id: number) {
        return this.crudHelper.makeRequest<ProjectAndRelatedData>("GetProjectAndRelated", { id }).pipe(take(1));
    }
}
